import React, {createContext, PropsWithChildren, useContext, useMemo} from 'react';
import {useLocalStorage} from '../hooks/useLocalStorage';
import {getEnvironment} from '../utils/envTest';

export type Debug = {
  forms: boolean | undefined;
  panels: boolean | undefined;
  precursor: boolean | undefined;
  accountDebugTab: boolean | undefined;
  editorGridEmpty: boolean | undefined;
  editorGridFailure: boolean | undefined;
  editorGridLoading: boolean | undefined;
  staffId: string | undefined;
  permissions: boolean | undefined;
};

interface DebugContextValue {
  debug: Debug;
  setDebug: React.Dispatch<React.SetStateAction<Debug>>;
}

export const DebugContext = createContext({} as DebugContextValue);
export const useDebugContext = () => useContext(DebugContext);

const DebugProvider: React.FC<PropsWithChildren> = ({children}) => {
  const [debug, setDebug] = useLocalStorage<Debug>('debug', {
    forms: false,
    panels: false,
    precursor: false,
    accountDebugTab: false,
    editorGridEmpty: false,
    editorGridFailure: false,
    editorGridLoading: false,
    permissions: false,
    staffId: undefined,
  });

  const value = useMemo(() => {
    return {
      debug,
      setDebug,
    };
  }, [debug, setDebug]);

  return <DebugContext.Provider value={value}>{children}</DebugContext.Provider>;
};

export const ShowIfDebugWrapper: React.FC<
  PropsWithChildren<{
    debugKey?: keyof Debug;
    showInDev?: boolean;
    hideInProd?: true;
    hideInUAT?: true;
  }>
> = ({
  debugKey = 'forms',
  children,
  showInDev = false,
  hideInProd = false,
  hideInUAT = false,
}) => {
  const {debug} = useDebugContext();
  const env = getEnvironment();

  if (showInDev && env === 'DEV') {
    return <>{children}</>;
  }

  if (hideInProd && env === 'PROD') {
    return null;
  }

  if (hideInUAT && env === 'UAT') {
    return null;
  }

  if (!debug || !debug[debugKey]) {
    return null;
  }

  return <>{children}</>;
};

export default DebugProvider;
